// What? A React component for the demo page interface.
// Why? To provide a demonstration page showcasing various UI components and layouts.
// How? The component's logic flow:
// 1. Initialize: Set up state for form data and table data
// 2. UI Management: Render various JoyUI components in a structured layout
// 3. Form Handling: Manage form state and validation
// 4. Table Display: Show sample data in a table format
// 5. Navigation: Provide action buttons for demonstration purposes

import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Select,
  Option,
  FormLabel,
  FormControl,
  IconButton,
  Input,
  Textarea,
  Checkbox,
  Switch,
  Slider,
  RadioGroup,
  Radio,
  Chip,
  Table,
  Typography,
  Stack,
  Alert,
  Card,
  CardContent,
  Grid,
  Sheet,
  Modal,
  ModalDialog,
  DialogTitle,
  Menu,
  MenuItem,
  ListItemDecorator,
} from "@mui/joy";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";

import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import { useMenuOutsideClick } from "../hooks/useMenuOutsideClick";

interface DemoData {
  id: number;
  name: string;
  status: string;
  category: string;
  priority: string;
  createdDate: string;
}

const DemoPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    name: "",
    description: "",
    category: "development",
  });

  // Menu state for demo
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    priority: "medium",
    isActive: true,
    notifications: false,
    rating: 3,
    type: "option1",
    tags: [] as string[],
  });

  const [tableData] = useState<DemoData[]>([
    {
      id: 1,
      name: "Project Alpha",
      status: "Active",
      category: "Development",
      priority: "High",
      createdDate: "2024-01-15",
    },
    {
      id: 2,
      name: "Project Beta",
      status: "Pending",
      category: "Design",
      priority: "Medium",
      createdDate: "2024-01-20",
    },
    {
      id: 3,
      name: "Project Gamma",
      status: "Completed",
      category: "Marketing",
      priority: "Low",
      createdDate: "2024-01-10",
    },
    {
      id: 4,
      name: "Project Delta",
      status: "Active",
      category: "Development",
      priority: "High",
      createdDate: "2024-01-25",
    },
    {
      id: 5,
      name: "Project Epsilon",
      status: "On Hold",
      category: "Research",
      priority: "Medium",
      createdDate: "2024-01-18",
    },
  ]);

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const availableTags = [
    "React",
    "TypeScript",
    "JoyUI",
    "Frontend",
    "Backend",
    "API",
    "Database",
    "Testing",
  ];

  // Simulate loading for demo purposes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleModalFormChange = (field: string, value: any) => {
    setModalFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleModalSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    console.log("Modal form submitted:", modalFormData);
    setIsModalOpen(false);
    // Reset form
    setModalFormData({
      name: "",
      description: "",
      category: "development",
    });
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuAction = (action: string) => {
    setSelectedAction(action);
    console.log("Menu action selected:", action);
    setMenuAnchor(null);
  };

  // Use the reusable hook for outside click handling
  useMenuOutsideClick(menuAnchor, handleMenuClose, menuAnchor);

  // Component for individual data cards (mobile view)
  const DataCard = ({ data }: { data: DemoData }) => (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "sm",
          borderColor: "primary.300",
        },
      }}
    >
      <Stack spacing={2}>
        {/* Top row: Name and Status */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              level="title-sm"
              sx={{
                fontWeight: 600,
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {data.name}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              <Typography
                component="span"
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 500 }}
              >
                ID:{" "}
              </Typography>
              {data.id}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              level="body-xs"
              color="neutral"
              sx={{ fontWeight: 500 }}
            >
              Status:
            </Typography>
            <Chip
              size="sm"
              variant="soft"
              color={getStatusColor(data.status) as any}
            >
              {data.status}
            </Chip>
          </Box>
        </Stack>

        {/* Middle row: Category and Priority */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              level="body-xs"
              color="neutral"
              sx={{ fontWeight: 500 }}
            >
              Category:
            </Typography>
            <Typography level="body-sm">{data.category}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              level="body-xs"
              color="neutral"
              sx={{ fontWeight: 500 }}
            >
              Priority:
            </Typography>
            <Chip
              size="sm"
              variant="soft"
              color={getPriorityColor(data.priority) as any}
            >
              {data.priority}
            </Chip>
          </Box>
        </Stack>

        {/* Bottom row: Date and Actions */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography level="body-xs" color="neutral">
              <Typography
                component="span"
                level="body-xs"
                color="neutral"
                sx={{ fontWeight: 500 }}
              >
                Created:{" "}
              </Typography>
              {data.createdDate}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton size="sm" variant="plain" color="primary">
              <EditIcon />
            </IconButton>
            <IconButton size="sm" variant="plain" color="danger">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "completed":
        return "primary";
      case "on hold":
        return "neutral";
      default:
        return "neutral";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "danger";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "neutral";
    }
  };

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Demo Page</Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            startDecorator={<AddIcon />}
            onClick={() => setIsModalOpen(true)}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            Add Item
          </Button>
          <Button
            startDecorator={<UploadIcon />}
            variant="outlined"
            onClick={() => console.log("Upload clicked")}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            Upload
          </Button>
          <Button
            startDecorator={<SettingsIcon />}
            variant="outlined"
            onClick={handleMenuOpen}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            Actions
          </Button>
          <Button
            startDecorator={<DownloadIcon />}
            color="neutral"
            variant="outlined"
            onClick={() => console.log("Export clicked")}
            sx={{
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            Export
          </Button>
        </Stack>
      </PageHeader>

      {/* Demo Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        placement="bottom-end"
        data-menu="true"
      >
        <MenuItem onClick={() => handleMenuAction("edit")}>
          <ListItemDecorator>
            <EditIcon />
          </ListItemDecorator>
          Edit Item
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("duplicate")}>
          <ListItemDecorator>
            <AddIcon />
          </ListItemDecorator>
          Duplicate Item
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("export")}>
          <ListItemDecorator>
            <DownloadIcon />
          </ListItemDecorator>
          Export Data
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("delete")} color="danger">
          <ListItemDecorator>
            <DeleteIcon />
          </ListItemDecorator>
          Delete Item
        </MenuItem>
      </Menu>

      {/* Show selected action */}
      {selectedAction && (
        <Alert color="success" sx={{ mb: 2 }}>
          Action selected: {selectedAction}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Overview Cards Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 3 }}>
                  Overview
                </Typography>
                <Grid container spacing={3}>
                  <Grid xs={12} md={4}>
                    <Card variant="soft" color="primary">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TrendingUpIcon
                            sx={{ fontSize: 40, color: "primary.500" }}
                          />
                          <Box>
                            <Typography level="h4">5</Typography>
                            <Typography level="body-sm">
                              Total Projects
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Card variant="soft" color="success">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AssignmentIcon
                            sx={{ fontSize: 40, color: "success.500" }}
                          />
                          <Box>
                            <Typography level="h4">2</Typography>
                            <Typography level="body-sm">
                              Active Projects
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Card variant="soft" color="warning">
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <PeopleIcon
                            sx={{ fontSize: 40, color: "warning.500" }}
                          />
                          <Box>
                            <Typography level="h4">12</Typography>
                            <Typography level="body-sm">
                              Team Members
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>

        {/* Form Controls Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 3 }}>
                  Form Controls
                </Typography>
                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) =>
                          handleFormChange("name", e.target.value)
                        }
                        placeholder="Enter name"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel>Category</FormLabel>
                      <Select
                        value={formData.category}
                        onChange={(_, value) =>
                          handleFormChange("category", value)
                        }
                        placeholder="Select category"
                      >
                        <Option value="development">Development</Option>
                        <Option value="design">Design</Option>
                        <Option value="marketing">Marketing</Option>
                        <Option value="research">Research</Option>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12}>
                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        value={formData.description}
                        onChange={(e) =>
                          handleFormChange("description", e.target.value)
                        }
                        placeholder="Enter description"
                        minRows={3}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel>Priority</FormLabel>
                      <RadioGroup
                        value={formData.priority}
                        onChange={(e) =>
                          handleFormChange("priority", e.target.value)
                        }
                      >
                        <Radio value="low" label="Low" />
                        <Radio value="medium" label="Medium" />
                        <Radio value="high" label="High" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl>
                      <FormLabel>Rating</FormLabel>
                      <Slider
                        value={formData.rating}
                        onChange={(_: Event, value: number | number[]) =>
                          handleFormChange("rating", value)
                        }
                        min={1}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl orientation="horizontal">
                      <Checkbox
                        checked={formData.isActive}
                        onChange={(e) =>
                          handleFormChange("isActive", e.target.checked)
                        }
                        label="Active"
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <FormControl orientation="horizontal">
                      <FormLabel>Notifications</FormLabel>
                      <Switch
                        checked={formData.notifications}
                        onChange={(e) =>
                          handleFormChange("notifications", e.target.checked)
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12}>
                    <FormControl>
                      <FormLabel>Tags</FormLabel>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {availableTags.map((tag) => (
                          <Chip
                            key={tag}
                            variant={
                              selectedTags.includes(tag) ? "solid" : "outlined"
                            }
                            color={
                              selectedTags.includes(tag) ? "primary" : "neutral"
                            }
                            onClick={() => handleTagToggle(tag)}
                            sx={{ cursor: "pointer" }}
                          >
                            {tag}
                          </Chip>
                        ))}
                      </Box>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>

        {/* Data Table Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 3 }}
                  spacing={2}
                >
                  <Typography level="h3">Data Table</Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent={{ xs: "center", sm: "flex-end" }}
                  >
                    <IconButton size="sm" variant="outlined">
                      <SearchIcon />
                    </IconButton>
                    <IconButton size="sm" variant="outlined">
                      <FilterIcon />
                    </IconButton>
                    <IconButton size="sm" variant="outlined">
                      <RefreshIcon />
                    </IconButton>
                  </Stack>
                </Stack>
                {/* Desktop Table View */}
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    overflow: "auto",
                  }}
                >
                  <Sheet sx={{ overflow: "auto" }}>
                    <Table sx={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={{ width: "60px" }}>ID</th>
                          <th>Name</th>
                          <th style={{ width: "120px" }}>Status</th>
                          <th style={{ width: "120px" }}>Category</th>
                          <th style={{ width: "100px" }}>Priority</th>
                          <th style={{ width: "140px" }}>Created Date</th>
                          <th style={{ width: "120px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row) => (
                          <tr key={row.id}>
                            <td>{row.id}</td>
                            <td>{row.name}</td>
                            <td>
                              <Chip
                                size="sm"
                                variant="soft"
                                color={getStatusColor(row.status) as any}
                              >
                                {row.status}
                              </Chip>
                            </td>
                            <td>{row.category}</td>
                            <td>
                              <Chip
                                size="sm"
                                variant="soft"
                                color={getPriorityColor(row.priority) as any}
                              >
                                {row.priority}
                              </Chip>
                            </td>
                            <td>{row.createdDate}</td>
                            <td>
                              <Stack direction="row" spacing={1}>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  color="danger"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Sheet>
                </Box>

                {/* Mobile Card View */}
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <Stack spacing={2}>
                    {tableData.map((data) => (
                      <DataCard key={data.id} data={data} />
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* Information Cards Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 3 }}>
                  Information Cards
                </Typography>
                <Grid container spacing={3}>
                  <Grid xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography level="h4" startDecorator={<InfoIcon />}>
                          Statistics
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          <Typography level="body-sm">
                            Total Items: 5
                          </Typography>
                          <Typography level="body-sm">Active: 2</Typography>
                          <Typography level="body-sm">Completed: 1</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography level="h4" startDecorator={<SearchIcon />}>
                          Search
                        </Typography>
                        <Input
                          placeholder="Search items..."
                          startDecorator={<SearchIcon />}
                          sx={{ mt: 2 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography level="h4" startDecorator={<FilterIcon />}>
                          Filters
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Chip size="sm" variant="soft" sx={{ mr: 1, mb: 1 }}>
                            Active
                          </Chip>
                          <Chip size="sm" variant="soft" sx={{ mr: 1, mb: 1 }}>
                            High Priority
                          </Chip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 3 }}>
                  Notifications
                </Typography>
                <Stack spacing={2}>
                  <Alert color="success" startDecorator={<InfoIcon />}>
                    Success! Your changes have been saved successfully.
                  </Alert>
                  <Alert color="warning" startDecorator={<InfoIcon />}>
                    Warning: Some items require attention.
                  </Alert>
                  <Alert color="danger" startDecorator={<InfoIcon />}>
                    Error: Unable to process your request.
                  </Alert>
                  <Alert color="neutral" startDecorator={<InfoIcon />}>
                    Information: This is an informational message.
                  </Alert>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => console.log("Cancel clicked")}
                >
                  Cancel
                </Button>
                <Button
                  startDecorator={<RefreshIcon />}
                  variant="outlined"
                  onClick={() => console.log("Reset clicked")}
                >
                  Reset
                </Button>
                <Button
                  color="primary"
                  onClick={() => console.log("Save clicked")}
                >
                  Save Changes
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Empty State Example Section */}
        <Card>
          <CardContent>
            {loading ? (
              <Loading />
            ) : (
              <>
                <Typography level="h3" sx={{ mb: 3 }}>
                  Empty State Example
                </Typography>
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography level="body-sm" color="neutral">
                    No items found matching your criteria.
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Simple Modal Example */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{
            maxWidth: 400,
            width: "100%",
          }}
        >
          <DialogTitle>Add New Item</DialogTitle>

          <form onSubmit={handleModalSubmit}>
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  name="name"
                  autoFocus
                  required
                  placeholder="Enter item name"
                  value={modalFormData.name}
                  onChange={(e) =>
                    handleModalFormChange("name", e.target.value)
                  }
                  size="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  placeholder="Enter description"
                  value={modalFormData.description}
                  onChange={(e) =>
                    handleModalFormChange("description", e.target.value)
                  }
                  minRows={3}
                  size="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Category</FormLabel>
                <Select
                  name="category"
                  value={modalFormData.category}
                  onChange={(_, value) =>
                    handleModalFormChange("category", value)
                  }
                  placeholder="Select category"
                  size="md"
                >
                  <Option value="development">Development</Option>
                  <Option value="design">Design</Option>
                  <Option value="marketing">Marketing</Option>
                  <Option value="research">Research</Option>
                </Select>
              </FormControl>

              {/* Button container with proper spacing */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ pt: 1 }}
              >
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => setIsModalOpen(false)}
                  size="md"
                >
                  Cancel
                </Button>
                <Button variant="solid" color="primary" type="submit" size="md">
                  Add Item
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default DemoPage;
